import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  Image as ImageIcon,
  Video,
  Paperclip,
  X,
  Loader2,
  Download,
  Eye,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import {
  useJournalEntryAttachments,
  useUploadJournalAttachment,
  useDetachJournalEntryDocument,
  useJournalAmendmentAttachments,
  useUploadJournalAmendmentAttachment,
  useDetachJournalAmendmentDocument,
  validateJournalAttachmentFile,
  type JournalAttachmentRow,
  type JournalAmendmentAttachmentRow,
} from '@/hooks/useJournalAttachments';
import { useGetDocumentDownloadUrl } from '@/hooks/useDocuments';
import {
  FilePreviewOverlay,
  type FilePreviewSource,
} from '@/components/documents/FilePreviewOverlay';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function attachmentIcon(mimeType: string) {
  if (mimeType.startsWith('video/')) return Video;
  if (mimeType.startsWith('image/')) return ImageIcon;
  return FileText;
}

interface JournalAttachmentsProps {
  clinicId: string;
  patientId: string;
  entryId?: string | null;
  amendmentId?: string | null;
  pendingFiles?: File[];
  onPendingFilesChange?: (files: File[]) => void;
  canEdit?: boolean;
  variant?: 'default' | 'read';
}

type AttachmentListItem = {
  id: string;
  document_id: string;
  document: JournalAttachmentRow['document'];
};

function fileKindLabel(mimeType: string, filename: string): string {
  if (mimeType === 'application/pdf' || filename.toLowerCase().endsWith('.pdf')) return 'PDF';
  if (mimeType.startsWith('image/')) return (mimeType.split('/')[1] ?? 'image').toUpperCase();
  if (mimeType.startsWith('video/')) return 'Video';
  const ext = filename.split('.').pop();
  return ext ? ext.toUpperCase() : '';
}

export function JournalAttachments({
  clinicId,
  patientId,
  entryId,
  amendmentId,
  pendingFiles = [],
  onPendingFilesChange,
  canEdit = false,
  variant = 'default',
}: JournalAttachmentsProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const entryAttachmentsQuery = useJournalEntryAttachments(amendmentId ? null : (entryId ?? null));
  const amendmentAttachmentsQuery = useJournalAmendmentAttachments(amendmentId ?? null);
  const uploadEntryAttachment = useUploadJournalAttachment(clinicId);
  const uploadAmendmentAttachment = useUploadJournalAmendmentAttachment(clinicId);
  const detachEntryAttachment = useDetachJournalEntryDocument(clinicId);
  const detachAmendmentAttachment = useDetachJournalAmendmentDocument(clinicId);
  const getDownloadUrl = useGetDocumentDownloadUrl();
  const [preview, setPreview] = useState<FilePreviewSource | null>(null);

  const isPendingMode = !entryId && !amendmentId && Boolean(onPendingFilesChange);
  const showFilePicker = canEdit || isPendingMode;

  const handleFilesSelected = async (files: FileList | null) => {
    if (!files?.length) return;

    for (const file of Array.from(files)) {
      const validationError = validateJournalAttachmentFile(file);
      if (validationError) {
        toast.error(t(`journal.attachments.errors.${validationError}`));
        continue;
      }

      if (isPendingMode) {
        onPendingFilesChange?.([...pendingFiles, file]);
        continue;
      }

      if (amendmentId) {
        try {
          await uploadAmendmentAttachment.mutateAsync({
            clinicId,
            patientId,
            amendmentId,
            file,
          });
          toast.success(t('journal.attachments.uploaded'));
        } catch {
          toast.error(t('journal.attachments.errors.upload_failed'));
        }
        continue;
      }

      if (!entryId) continue;

      try {
        await uploadEntryAttachment.mutateAsync({
          clinicId,
          patientId,
          journalEntryId: entryId,
          file,
        });
        toast.success(t('journal.attachments.uploaded'));
      } catch {
        toast.error(t('journal.attachments.errors.upload_failed'));
      }
    }

    if (inputRef.current) inputRef.current.value = '';
  };

  const handleView = (source: FilePreviewSource) => {
    setPreview(source);
  };

  const handleDownload = async (documentId: string) => {
    try {
      const url = await getDownloadUrl.mutateAsync(documentId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      toast.error(t('journal.attachments.errors.download_failed'));
    }
  };

  const handleRemovePending = (index: number) => {
    if (!onPendingFilesChange) return;
    onPendingFilesChange(pendingFiles.filter((_, i) => i !== index));
  };

  const handleDetach = async (attachment: AttachmentListItem) => {
    try {
      if (amendmentId) {
        await detachAmendmentAttachment.mutateAsync({
          attachment_id: attachment.id,
          amendment_id: amendmentId,
        });
      } else if (entryId) {
        await detachEntryAttachment.mutateAsync({
          attachment_id: attachment.id,
          journal_entry_id: entryId,
        });
      } else {
        return;
      }
      toast.success(t('journal.attachments.removed'));
    } catch {
      toast.error(t('journal.attachments.errors.remove_failed'));
    }
  };

  const attachments: AttachmentListItem[] = amendmentId
    ? (amendmentAttachmentsQuery.data ?? []).map((row: JournalAmendmentAttachmentRow) => ({
        id: row.id,
        document_id: row.document_id,
        document: row.document,
      }))
    : (entryAttachmentsQuery.data ?? []);
  const attachmentsQuery = amendmentId ? amendmentAttachmentsQuery : entryAttachmentsQuery;
  const isBusy =
    uploadEntryAttachment.isPending ||
    uploadAmendmentAttachment.isPending ||
    detachEntryAttachment.isPending ||
    detachAmendmentAttachment.isPending ||
    getDownloadUrl.isPending;
  const isReadOnly = !showFilePicker;
  const hasItems = attachments.length > 0 || pendingFiles.length > 0;
  // Read mode must never render the "Vedhæftede filer" heading, upload hint,
  // empty box or add button. The default chrome still appears while loading
  // or when files exist, which is why the Journal tab showed those strings.
  if (variant === 'read') {
    if (attachments.length === 0) {
      return preview ? <FilePreviewOverlay source={preview} onClose={() => setPreview(null)} /> : null;
    }
    return (
      <>
        {preview ? <FilePreviewOverlay source={preview} onClose={() => setPreview(null)} /> : null}
        <ul className="jf-files">
        {attachments.map((attachment) => {
          const Icon = attachmentIcon(attachment.document.mime_type);
          const kind = fileKindLabel(
            attachment.document.mime_type,
            attachment.document.original_filename,
          );
          const meta = [kind, formatFileSize(attachment.document.size_bytes)]
            .filter(Boolean)
            .join(' · ');
          return (
            <li key={attachment.id} className="jf-file">
              <Icon strokeWidth={1.75} aria-hidden />
              <div className="nm">
                <b title={attachment.document.original_filename}>
                  {attachment.document.original_filename}
                </b>
                {meta ? <span>{meta}</span> : null}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="btn-icon"
                title={t('journal.attachments.view')}
                onClick={() =>
                  handleView({
                    type: 'document',
                    documentId: attachment.document_id,
                    filename: attachment.document.original_filename,
                    mimeType: attachment.document.mime_type,
                  })
                }
              >
                <Eye strokeWidth={1.75} />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="btn-icon"
                title={t('journal.attachments.download')}
                onClick={() => void handleDownload(attachment.document_id)}
              >
                <Download strokeWidth={1.75} />
              </Button>
            </li>
          );
        })}
      </ul>
      </>
    );
  }
  if (isReadOnly && !attachmentsQuery.isLoading && !hasItems) {
    return preview ? <FilePreviewOverlay source={preview} onClose={() => setPreview(null)} /> : null;
  }

  return (
    <div>
      {preview && <FilePreviewOverlay source={preview} onClose={() => setPreview(null)} />}
      <div className="mb-2 flex items-center justify-between gap-2">
        <label className="block text-sm font-medium text-ds-text">
          {t('journal.attachments.title')}
        </label>
        {showFilePicker && (
          <>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.webm,.avi,application/pdf,image/*,video/*"
              className="hidden"
              onChange={(e) => void handleFilesSelected(e.target.files)}
            />
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={isBusy}
              onClick={() => inputRef.current?.click()}
            >
              {uploadEntryAttachment.isPending || uploadAmendmentAttachment.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Paperclip className="h-4 w-4" />
              )}
              {t('journal.attachments.add')}
            </Button>
          </>
        )}
      </div>

      {showFilePicker ? (
        <p className="mb-2 text-xs text-ds-muted">{t('journal.attachments.hint')}</p>
      ) : null}

      {(entryId || amendmentId) && attachmentsQuery.isLoading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-ds-muted" />
        </div>
      ) : (
        <ul className="space-y-2">
          {attachments.map((attachment) => {
            const Icon = attachmentIcon(attachment.document.mime_type);
            return (
              <li
                key={attachment.id}
                className="flex items-center gap-2 rounded-lg border border-ds-line bg-ds-sunk px-3 py-2 bg-ds-card/60"
              >
                <Icon className="h-4 w-4 shrink-0 text-ds-muted" />
                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() =>
                      handleView({
                        type: 'document',
                        documentId: attachment.document_id,
                        filename: attachment.document.original_filename,
                        mimeType: attachment.document.mime_type,
                      })
                    }
                    className="block w-full truncate text-left text-sm text-ds-text hover:text-ds-teal"
                  >
                    {attachment.document.original_filename}
                  </button>
                  <p className="text-xs text-ds-muted">
                    {formatFileSize(attachment.document.size_bytes)}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="tertiary"
                  size="icon"
                  title={t('journal.attachments.view')}
                  onClick={() =>
                    handleView({
                      type: 'document',
                      documentId: attachment.document_id,
                      filename: attachment.document.original_filename,
                      mimeType: attachment.document.mime_type,
                    })
                  }
                >
                  <Eye />
                </Button>
                <Button
                  type="button"
                  variant="tertiary"
                  size="icon"
                  title={t('journal.attachments.download')}
                  onClick={() => void handleDownload(attachment.document_id)}
                >
                  <Download />
                </Button>
                {canEdit && (entryId || amendmentId) && (
                  <Button
                    type="button"
                    variant="danger"
                    size="icon"
                    title={t('journal.attachments.remove')}
                    onClick={() => void handleDetach(attachment)}
                  >
                    <X />
                  </Button>
                )}
              </li>
            );
          })}

          {pendingFiles.map((file, index) => {
            const mime = file.type || 'application/octet-stream';
            const Icon = attachmentIcon(mime);
            return (
              <li
                key={`${file.name}-${index}`}
                className="flex items-center gap-2 rounded-lg border border-dashed border-ds-field bg-ds-card px-3 py-2"
              >
                <Icon className="h-4 w-4 shrink-0 text-ds-muted" />
                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() => handleView({ type: 'local', file })}
                    className="block w-full truncate text-left text-sm text-ds-text hover:text-ds-teal"
                  >
                    {file.name}
                  </button>
                  <p className="text-xs text-ds-muted">
                    {formatFileSize(file.size)} · {t('journal.attachments.pending')}
                  </p>
                </div>
                {onPendingFilesChange && (
                  <Button
                    type="button"
                    variant="tertiary"
                    size="icon"
                    title={t('journal.attachments.view')}
                    onClick={() => handleView({ type: 'local', file })}
                  >
                    <Eye />
                  </Button>
                )}
                {onPendingFilesChange && (
                  <Button
                    type="button"
                    variant="danger"
                    size="icon"
                    title={t('journal.attachments.remove')}
                    onClick={() => handleRemovePending(index)}
                  >
                    <X />
                  </Button>
                )}
              </li>
            );
          })}

          {!isReadOnly && !attachments.length && !pendingFiles.length && (
            <li className="rounded-lg border border-dashed border-ds-line px-3 py-4 text-center text-sm text-ds-muted">
              {t('journal.attachments.empty')}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
